import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";
import { api, projectPoster } from "../../lib/api";
import { Field, TextInput, TextArea } from "./fields";

const empty = { title: "", category: "", description: "", thumbnail: "", video_url: "", client: "", format: "short" };

export const ProjectForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, thumbnail: data.url }));
      toast.success("Thumbnail uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/projects/${initial.id}`, form);
      else await api.post("/projects", form);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border p-6" style={{ borderColor: "var(--border-color)", background: "var(--surface-color)" }} data-testid="project-form">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-lg font-medium">{initial?.id ? "Edit Project" : "New Project"}</h3>
        <button onClick={onCancel} className="p-1 opacity-70 hover:opacity-100" data-testid="project-form-close"><X size={20} /></button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Title (internal)"><TextInput value={form.title} onChange={set("title")} data-testid="pf-title" /></Field>
        <Field label="Format">
          <select
            value={form.format}
            onChange={set("format")}
            className="w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm outline-none focus:border-[var(--accent-color)] transition-colors"
            style={{ borderColor: "var(--border-color)" }}
            data-testid="pf-format"
          >
            <option value="short" style={{ color: "#000" }}>Short Form (9:16)</option>
            <option value="long" style={{ color: "#000" }}>Long Form (16:9)</option>
          </select>
        </Field>
        <Field label="Category (optional)"><TextInput value={form.category} onChange={set("category")} data-testid="pf-category" /></Field>
        <Field label="Client / Project (optional)"><TextInput value={form.client} onChange={set("client")} data-testid="pf-client" /></Field>
        <div className="md:col-span-2">
          <Field label="Description (optional)"><TextArea value={form.description} onChange={set("description")} data-testid="pf-description" /></Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Video URL" hint="YouTube, Vimeo or direct .mp4"><TextInput value={form.video_url} onChange={set("video_url")} data-testid="pf-video" placeholder="https://youtube.com/watch?v=…" /></Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Thumbnail" hint="Paste an image URL or upload a file">
            <div className="flex flex-col sm:flex-row gap-3">
              <TextInput value={form.thumbnail} onChange={set("thumbnail")} data-testid="pf-thumbnail" placeholder="https://…" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="pf-file" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border px-4 py-2.5 text-sm hover:border-[var(--accent-color)] transition-colors disabled:opacity-60"
                style={{ borderColor: "var(--border-color)" }}
                data-testid="pf-upload-btn"
              >
                <Upload size={16} /> {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </Field>
          {(() => {
            const preview = projectPoster(form);
            if (!preview.src) return null;
            return (
              <div className="mt-4" data-testid="pf-thumb-preview">
                <img src={preview.src} alt="preview" className="h-40 w-full max-w-sm rounded-lg border object-cover" style={{ borderColor: "var(--border-color)" }} />
                {preview.fromYouTube && (
                  <p className="mt-2 text-xs" style={{ color: "var(--text-secondary)" }}>
                    Using YouTube thumbnail automatically (no uploaded thumbnail). Upload one to override.
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={save} disabled={saving} className="rounded-full px-6 py-2.5 text-sm font-medium text-black hover:opacity-90 disabled:opacity-60" style={{ background: "var(--accent-color)" }} data-testid="pf-save">
          {saving ? "Saving…" : "Save Project"}
        </button>
        <button onClick={onCancel} className="rounded-full border px-6 py-2.5 text-sm hover:border-[var(--accent-color)] transition-colors" style={{ borderColor: "var(--border-color)" }} data-testid="pf-cancel">Cancel</button>
      </div>
    </div>
  );
};
